/**
 * How a plan's money and dates are written.
 *
 * Their own file rather than sitting beside the mock rows they started next
 * to: the plan layer maps every response through `formatCreatedOn`, and a
 * module of real API mapping reaching into `data/plansMock` would be what
 * keeps that mock alive long after the list endpoint has replaced it.
 */

/** Rupees are grouped the Indian way — ₹ 1,22,499, not ₹ 122,499. */
export const formatPrice = (price: number) =>
  `₹ ${price.toLocaleString("en-IN")}`;

/**
 * What a plan includes, as the line under its name in the table reads.
 *
 * One entry per count the plan puts a number on, and nothing at all for the
 * ones it leaves unset — a count of `null` is a plan that does not say,
 * which has nothing to write, while a 0 is a plan that says none and is
 * worth writing. A plan answering none of the three gets an empty list, and
 * the table draws no second line for it.
 */
export const planInclusions = (plan: {
  consultations: number | null;
  mealPlans: number | null;
  mealOptionsPerMeal: number | null;
}): string[] => {
  const parts: string[] = [];

  const add = (count: number | null, one: string, many: string) => {
    if (count === null) return;
    parts.push(`${count} ${count === 1 ? one : many}`);
  };

  add(plan.consultations, "Consultation", "Consultations");
  add(plan.mealPlans, "Meal plan", "Meal plans");
  add(plan.mealOptionsPerMeal, "Option per meal", "Options per meal");

  return parts;
};

/** The date format the "Created on" column is written in, e.g. 1 Jul 2026. */
export const formatCreatedOn = (date: Date) =>
  date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
